import { CANALES, CANAL_LABEL, type CanalSedeRow } from '../../lib/api.js';
import { fmtInt, fmtPct } from '../../lib/format.js';

/**
 * Matriz canal × sede: cómo vende cada hotel.
 *
 * Es una tabla, no un gráfico: los valores exactos importan (son las noches con
 * las que se mide a cada equipo comercial) y además hace de vista tabular
 * accesible de los dos gráficos de arriba. Debajo de cada cifra va el peso de
 * ese canal DENTRO de su hotel, que es lo comparable entre sedes de tamaños muy
 * distintos; el número siempre va en tinta de texto, así que se lee aunque el
 * tinte de fondo no se distinga.
 */
export function CanalesSedeMatrix({ rows }: { rows: CanalSedeRow[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">Sin noches por canal para el período.</p>;
  }

  const totales = CANALES.map((canal) => rows.reduce((s, r) => s + (r.porCanal[canal] ?? 0), 0));
  const totalGeneral = totales.reduce((s, v) => s + v, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-700 text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-3 text-left font-medium">Sede</th>
            {CANALES.map((canal) => (
              <th key={canal} className="px-2 py-2 text-right font-medium">
                {CANAL_LABEL[canal]}
              </th>
            ))}
            <th className="py-2 pl-3 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.sede} className="border-b border-slate-800/80">
              <td className="py-1.5 pr-3 font-medium text-slate-200">{r.sede}</td>
              {CANALES.map((canal) => {
                const v = r.porCanal[canal] ?? 0;
                const mix = r.mix[canal];
                return (
                  <td
                    key={canal}
                    className="px-2 py-1.5 text-right tabular-nums text-slate-200"
                    style={
                      mix != null && mix > 0
                        ? { backgroundColor: `rgba(57, 135, 229, ${(0.1 + 0.5 * mix).toFixed(3)})` }
                        : undefined
                    }
                  >
                    {v === 0 ? (
                      <span className="text-slate-600">0</span>
                    ) : (
                      <>
                        {fmtInt(v)}
                        <span className="ml-1 text-[10px] text-slate-400">
                          {mix == null ? '' : fmtPct(mix * 100)}
                        </span>
                      </>
                    )}
                  </td>
                );
              })}
              <td className="py-1.5 pl-3 text-right font-medium tabular-nums text-slate-100">
                {fmtInt(r.total)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-700 text-slate-100">
            <td className="py-2 pr-3 text-xs uppercase tracking-wide text-slate-400">Cadena</td>
            {totales.map((v, i) => (
              <td key={CANALES[i]} className="px-2 py-2 text-right font-medium tabular-nums">
                {fmtInt(v)}
              </td>
            ))}
            <td className="py-2 pl-3 text-right font-semibold tabular-nums">
              {fmtInt(totalGeneral)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
